import { FeatureNames } from "~/modules/feature_flag/flags";

export enum ColumnTypes {
    string = "string",
    name = "name",
    number = "number",
    date = "date",
    datetime = "datetime",
    datetimeCompact = "datetime_compact",
    dollar = "dollar",
    token = "token",
    percent = "percent",
    percentChange = "percentChange",
    years = "years",
    timeAgo = "timeAgo",
    sparkline = "sparkline",
}
export type ColumnType = `${ColumnTypes}`;

interface CellValueFunction<RowType> {
    cellValue: (row: RowType) => string | number | undefined | null;
    render?: never;
}

type RenderOptions = Record<
    string,
    string | boolean | number | undefined | null
>;

interface CellRenderFunction<RowType, RenderOptions> {
    cellValue?: never;
    render: (
        row: RowType,
        cellProps: {
            size: RowSize;
            width: number;
            renderOptions?: RenderOptions;
        },
    ) => React.ReactNode;
}

type ColumnRenderer<RowType, RenderOptions> =
    CellValueFunction<RowType> | CellRenderFunction<RowType, RenderOptions>;

export enum ColumnColors {
    None = "none",
    Blue = "blue",
    Green = "green",
    Orange = "orange",
    Red = "red",
    Yellow = "yellow",
    Indigo = "indigo",
    Purple = "purple",
    Pink = "pink",
}

// ordered lowest to highest - a segment can do anything a lower segment can
export enum AccessSegment {
    Free = "free",
    Preview = "preview",
    Pro = "pro",
    Enterprise = "enterprise",
}

// minimum segment required for each capability
export type ColumnAccess = {
    view?: AccessSegment; // default: AccessSegment.Free
    sort?: AccessSegment; // default: same as view
};

// ENHANCEMENT - add "premium-only" behavior -> visible / hidden / blurred
export type ColumnMetadata<RowType, SortableColumns> = {
    slug: string;
    type: ColumnType;
    label: string | React.ReactNode;
    tooltip?: string;
    width: number;
    maxWidth?: number;
    sortsOn?: SortableColumns;
    renderOptions?: RenderOptions;
    cellColor?: ColumnColors | ((row: RowType) => ColumnColors | null);
    sticky?: {
        left: number;
        isLast?: boolean;
    };
    blur?: boolean;
    featureFlag?: {
        feature: FeatureNames;
        invert?: boolean;
    };
    access?: ColumnAccess;
} & ColumnRenderer<RowType, RenderOptions>;

export enum RowSizes {
    extraSmall = "xs",
    small = "sm",
    medium = "md",
    large = "lg",
    extraLarge = "xl",
}
export type RowSize = `${RowSizes}`;
